import React from "react";
import { cn } from "../../../../lib/utils";

/** Base animated skeleton block */
export const Skeleton = ({ className }) => (
  <div
    className={cn(
      "animate-pulse bg-gray-200 dark:bg-gray-700 rounded-sm",
      className
    )}
  />
);

/** Text line placeholder */
export const SkeletonText = ({ width = "w-32", height = "h-3", className }) => (
  <Skeleton className={cn(width, height, className)} />
);

/** Card-shaped skeleton with optional inner content */
export const SkeletonCard = ({ className, children }) => (
  <div
    className={cn(
      "bg-white dark:bg-gray-800 rounded-sm border border-gray-200 dark:border-gray-700 shadow-sm dark:shadow-none",
      className
    )}
  >
    {children}
  </div>
);

/** Skeleton matching HeroStatsBar — 4 stat cards */
export const DashboardHeroSkeleton = () => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
    {[...Array(4)].map((_, i) => (
      <SkeletonCard key={i} className="p-4 sm:p-5 relative overflow-hidden">
        <Skeleton className="h-1 w-8 absolute top-0 left-0" />
        <Skeleton className="h-8 sm:h-10 w-16 mt-2" />
        <Skeleton className="h-2.5 w-20 mt-3" />
      </SkeletonCard>
    ))}
  </div>
);

/** Skeleton matching Courses + Chart 7:5 grid */
export const DashboardContentSkeleton = () => (
  <div className="px-2 sm:px-4">
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
      {/* Courses column */}
      <div className="lg:col-span-7">
        <div className="mb-4">
          <Skeleton className="h-5 w-36" />
          <Skeleton className="h-1 w-12 mt-2" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[...Array(4)].map((_, i) => (
            <SkeletonCard key={i} className="overflow-hidden">
              <Skeleton className="h-20 sm:h-24 w-full rounded-none" />
              <div className="p-3 sm:p-4 space-y-2">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-full" />
              </div>
              <div className="px-3 sm:px-4 py-2.5 border-t border-gray-200 dark:border-gray-700">
                <Skeleton className="h-3 w-24" />
              </div>
            </SkeletonCard>
          ))}
        </div>
      </div>

      {/* Chart column */}
      <div className="lg:col-span-5">
        <div className="mb-4">
          <Skeleton className="h-5 w-40" />
          <Skeleton className="h-1 w-12 mt-2" />
        </div>
        <SkeletonCard className="p-4 sm:p-6 space-y-4">
          <Skeleton className="h-6 w-full" />
          <div className="space-y-3 mt-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="flex items-center gap-3">
                <Skeleton className="h-3 w-3" />
                <Skeleton className="h-3 w-20" />
                <Skeleton className="h-3 w-8 ml-auto" />
              </div>
            ))}
          </div>
        </SkeletonCard>
      </div>
    </div>
  </div>
);

/** Skeleton matching LiveClassStrip row */
export const DashboardLiveClassSkeleton = () => (
  <div className="px-2 sm:px-4">
    <div className="mb-4">
      <Skeleton className="h-5 w-32" />
      <Skeleton className="h-1 w-12 mt-2" />
    </div>
    <div className="flex gap-4 overflow-hidden">
      {[...Array(3)].map((_, i) => (
        <SkeletonCard key={i} className="flex-shrink-0 w-64 p-4 space-y-3">
          <Skeleton className="h-3 w-16" />
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-3 w-28" />
        </SkeletonCard>
      ))}
    </div>
  </div>
);

/** Skeleton matching AssignmentTable rows */
export const DashboardAssignmentSkeleton = () => (
  <div className="px-2 sm:px-4 pb-8">
    <div className="mb-4">
      <Skeleton className="h-5 w-44" />
      <Skeleton className="h-1 w-12 mt-2" />
    </div>
    <SkeletonCard className="overflow-hidden">
      {[...Array(4)].map((_, i) => (
        <div
          key={i}
          className={cn(
            "flex items-center gap-4 px-4 py-3.5",
            i > 0 && "border-t border-gray-200 dark:border-gray-700"
          )}
        >
          <Skeleton className="h-4 w-4 flex-shrink-0" />
          <div className="flex-1 space-y-1.5">
            <Skeleton className="h-3.5 w-48" />
            <Skeleton className="h-2.5 w-28" />
          </div>
          <Skeleton className="h-6 w-20" />
        </div>
      ))}
    </SkeletonCard>
  </div>
);

/** Skeleton matching AllAnnouncements — week heading + card rows with image */
export const AnnouncementListSkeleton = () => (
  <div className="space-y-8">
    {[...Array(2)].map((_, g) => (
      <div key={g}>
        <Skeleton className="h-3 w-28 mb-4" />
        <Skeleton className="h-px w-full mb-4" />
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <SkeletonCard key={i} className="flex overflow-hidden">
              <div className="flex-1 p-4 space-y-3">
                <Skeleton className="h-4 w-3/5" />
                <Skeleton className="h-3 w-28" />
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-4/5" />
              </div>
              <Skeleton className="w-64 shrink-0 rounded-none" />
            </SkeletonCard>
          ))}
        </div>
      </div>
    ))}
  </div>
);

/** Full dashboard skeleton — combines all sections */
export const DashboardSkeleton = () => (
  <div className="space-y-8 bg-gray-50 dark:bg-gray-900 min-h-screen">
    <DashboardHeroSkeleton />
    <DashboardContentSkeleton />
    <DashboardLiveClassSkeleton />
    <DashboardAssignmentSkeleton />
  </div>
);

/** Skeleton matching DiscussionList — 3 discussion cards */
export const DiscussionListSkeleton = () => (
  <div className="space-y-3">
    {[...Array(3)].map((_, i) => (
      <SkeletonCard key={i} className="flex overflow-hidden">
        <Skeleton className="w-1 self-stretch rounded-none" />
        <div className="flex-1 p-4 sm:p-5 space-y-3">
          <div className="flex items-center gap-3">
            <Skeleton className="w-8 h-8 sm:w-9 sm:h-9 flex-shrink-0" />
            <div className="space-y-1.5 flex-1">
              <Skeleton className="h-3.5 w-28" />
              <Skeleton className="h-2.5 w-16" />
            </div>
          </div>
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-3 w-full" />
          <div className="flex items-center gap-3 pt-3 border-t border-gray-200 dark:border-gray-700">
            <Skeleton className="h-3 w-3.5" />
            <Skeleton className="h-5 w-8" />
            <Skeleton className="h-3 w-16" />
          </div>
        </div>
      </SkeletonCard>
    ))}
  </div>
);

/** Skeleton for courseware course grid — 6 cards in 3-col layout */
export const CourseGridSkeleton = () => (
  <div>
    <div className="mb-4">
      <Skeleton className="h-5 w-48" />
      <Skeleton className="h-1 w-12 mt-2" />
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 md:gap-6">
      {[...Array(6)].map((_, i) => (
        <SkeletonCard key={i} className="overflow-hidden">
          <Skeleton className="h-20 sm:h-24 w-full rounded-none" />
          <div className="p-3 sm:p-4 space-y-2">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-full" />
          </div>
          <div className="px-3 sm:px-4 py-2.5 border-t border-gray-200 dark:border-gray-700">
            <Skeleton className="h-3 w-32" />
          </div>
        </SkeletonCard>
      ))}
    </div>
  </div>
);
